import React, { useCallback, useEffect, useState } from 'react';
import { Menu } from 'antd';
import * as S from './Styled';
import {
  fetchMenu,
  setProducts,
  setProductsLoader,
  setSelectedFilters,
} from '../../actions/category';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useLang } from '../../context/lang';
import Icon from '../Icon';

const calculateItemPosition = selector => {
  if (!selector) {
    return null;
  }

  let btn = document?.querySelector(selector)?.getBoundingClientRect();

  return {
    left: btn?.left ?? 0,
    top: btn?.top ?? 0,
  };
};

const relativeOffset = selector => {
  let bodyWidth = document.querySelector('body')?.getBoundingClientRect()
    ?.width;
  let containerWidth = document.querySelector(selector)?.getBoundingClientRect()
    ?.width;

  return (bodyWidth - containerWidth) / 2;
};

const CategoryMenu = ({ showMenu, setShowMenu }) => {
  const [currentCategory, setCurrentCategory] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const [leftCoordinate, setLeftCoordinate] = useState(0);

  const isMob = useMediaQuery({ query: '(max-width: 844px)' });
  const isNarrow = window.matchMedia('(max-width: 844px)').matches;
  const menuMode = isNarrow ? 'inline' : 'vertical';

  const dispatch = useDispatch();
  const history = useHistory();

  const { categories, selectedFilters } = useSelector(
    state => state.categories
  );

  const { language } = useLang();

  let getLocalized = item => item['name_' + language];

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchMenu());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleReset = () => {
      dispatch(setProductsLoader(true));
      dispatch(setProducts([]));
    };

    dispatch(
      setSelectedFilters({
        categories: [...currentCategory],
      })
    );
    handleReset();
  }, [currentCategory]);

  const recalculatePosition = useCallback(() => {
    const zoom =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--html-zoom'
        )
      ) || 1;

    const leftOffset =
      (calculateItemPosition('#cat-menu-trigger')?.left -
        relativeOffset('.container')) /
      zoom;

    if (leftOffset != null && !isNaN(leftOffset)) {
      setLeftCoordinate(leftOffset);
    }
  }, []);

  useEffect(() => {
    recalculatePosition();
    window.addEventListener('load', recalculatePosition);
    window.addEventListener('resize', recalculatePosition);

    return () => {
      window.removeEventListener('load', recalculatePosition);
      window.removeEventListener('resize', recalculatePosition);
    };
  }, [recalculatePosition]);

  useEffect(() => {
    if (showMenu) {
      recalculatePosition();
    }
  }, [showMenu, recalculatePosition]);

  useEffect(() => {
    if (selectedFilters?.categories?.length) {
      let categoryId = selectedFilters.categories[0];

      if (currentCategory.length) {
        history.replace({
          pathname: '/category',
          search: `?categories=${categoryId}`,
        });
      }
    }
  }, [selectedFilters.categories]);

  const handleMenuClick = (key, openKeys, setOpenKeys) => {
    setOpenKeys([key]);
  };

  const renderMenuItems = (data, openKeys, setOpenKeys, extraClassName) => {
    return data.map(menuItem => {
      if (menuItem.children.length > 0) {
        return (
          <S.SubMenu
            key={menuItem.id}
            title={getLocalized(menuItem)}
            onTitleClick={el => {
              handleMenuClick(menuItem.id, openKeys, setOpenKeys);

              let currentTarget = el.domEvent.currentTarget;
              let parent = currentTarget.parentNode;
              let isRootMenuItem = parent.classList.contains(extraClassName);

              if (isNarrow || isRootMenuItem) {
                return;
              }

              setCurrentCategory([el.key]);
              setShowMenu(false);
            }}
            className={extraClassName ? extraClassName : ''}
          >
            {renderMenuItems(menuItem.children, openKeys, setOpenKeys)}
          </S.SubMenu>
        );
      }
      return (
        <Menu.Item
          key={menuItem.id}
          onClick={el => {
            setShowMenu(false);
            setCurrentCategory([el.key]);
          }}
        >
          {getLocalized(menuItem)}
        </Menu.Item>
      );
    });
  };

  const onOpenChange = keys => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    const rootKeys = categories.map(category => category.id + '');

    if (rootKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const additionalProps = isNarrow
    ? {
        defaultOpenKeys: [],
        openKeys: openKeys,
        onOpenChange: keys => onOpenChange(keys),
      }
    : {
        subMenuOpenDelay: 1,
      };

  const triggerSubMenuAction = menuMode === 'vertical' ? 'hover' : 'click';

  return showMenu ? (
    <>
      <S.Container>
        <S.CloseBtn onClick={() => setShowMenu(false)}>
          <Icon name="close" />
        </S.CloseBtn>
        <S.MenuWrapper style={{ left: leftCoordinate }}>
          <S.Menu
            mode={menuMode}
            onSelect={() => {
              setShowMenu(false);
            }}
            {...additionalProps}
            triggerSubMenuAction={triggerSubMenuAction}
          >
            {renderMenuItems(
              categories,
              openKeys,
              setOpenKeys,
              'extra-classname-root'
            )}
          </S.Menu>
        </S.MenuWrapper>
      </S.Container>

      <S.Mask onClick={() => setShowMenu(false)} />
    </>
  ) : null;
};

export default CategoryMenu;
